import { Button, Row, Col, Typography, Divider, Space, notification, Card, Statistic, Table, Pagination, Tag, Grid, Input, Alert, Tabs, Modal, Empty, Segmented, DatePicker, Select } from "antd";
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import ModuleNav, { MODULE_NAVS } from '../../components/ModuleNav';
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import dayjs from "dayjs";
import { useAuth } from '../../assets/utils/useAuth';
import { usePermissions } from '../../assets/utils/usePermissions';
import useLocalStorage from '../../assets/utils/useLocal';
import {
  MedicineBoxOutlined,
  UnorderedListOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  ReloadOutlined,
  TableOutlined,
  AppstoreOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import './encounters-dashboard.css'
import DashboardHero from '../dashboard/components/DashboardHero';
import DashboardSection from '../dashboard/components/DashboardSection';
import { SearchResultsSkeleton } from '../search/SearchResultCards';
import { TableSkeleton } from '../dashboard/components/DashboardSkeleton';
import { EncounterListingCard } from './EncounterListingCard';
import EncountersQueueTable from './EncountersQueueTable';
import pmApi from '../planManagement/pmApi';
import { Checkbox } from 'antd';
import { EncounterDetailsModal } from './EncounterDetailsModal';
import useClaimsRollout from '../../hooks/useClaimsRollout';
import {
  NEW_FLOW_STATUS_OPTIONS,
  NEW_FLOW_STATUS_SCOPES,
  SUBMIT_CLAIM_LABEL,
  canCheckoutFor,
  canSubmitClaimFor,
} from './claimSubmission';
import lib, { myRequestObj } from "../../assets/utils/lib";
import { parseSortParams, toSortQuery, SORT_BY, SORT_DIR } from "../../utils/tableSortParams";
import { formatCheckoutEvidenceSummary } from '../preauth/paDisplayUtils';
import { useDispatch } from "react-redux";
import { selectclient } from "../../stores/verify";
import Loader from "../compoinents/loader";
import SearchWithAdvancedFeatures from "../compoinents/SearchWithDropdown";
import ConfigurableRangePicker from "../compoinents/ConfigurableRangePicker";
import { formatter } from "../dashboard";
import DebounceSelect from "../compoinents/DebouncedSelect";
import AdminCheckoutValidationChecker from "../../components/Admin/AdminCheckoutValidationChecker";
import CheckinCleanupPanel, { isCheckinCleanupSuperAdmin } from "../../components/Admin/CheckinCleanupPanel";
import {
  ENCOUNTER_CHECKOUT_PERMISSIONS,
  ENCOUNTER_FILE_CLAIM_PERMISSIONS,
  LEGACY_FILE_CLAIM_ROLES,
} from "./encounterPermissions";

// Admin checking out an encounter: checkout ONLY releases the client's active
// check-in status (revised encounter flow). It never files a claim and never
// deletes the encounter — every PA item being rejected (or no PA items at all)
// just means there is nothing to submit as a claim, and the encounter stays on
// the Encounters page. Switch on the envelope `code`, never on message text.
const ALL_PA_REJECTED_CODE = 'ALL_PA_ITEMS_REJECTED';

const formatNumber = (value) => new Intl.NumberFormat('en-NG').format(Number.isFinite(Number(value)) ? Number(value) : 0);

const formatDate = (value) => {
  if (!value) return 'Not Available';
  try {
    return new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium' }).format(new Date(value));
  } catch (error) {
    return value;
  }
};

const formatDateTime = (value) => {
  if (!value) return 'Not Available';
  try {
    return new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
  } catch (error) {
    return value;
  }
};

const FACILITY_QUEUE_STATUS = '0';
const CHECKIN_TYPE_FALLBACK = [
  { value: '1', label: 'In-Patient' },
  { value: '2', label: 'Out-Patient' },
];
const STATUS_OPTIONS = [
  { value: '0', label: 'Active' },
  { value: '1', label: 'Closed' },
];
const SOURCE_OPTIONS = [
  { value: '0', label: 'Facility' },
  { value: '1', label: 'Manual PA' },
];

const ymdFromParam = (value) => {
  if (!value) return '';
  const slice = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(slice) ? slice : '';
};

const readFacilityIdParam = (params) => {
  const raw = params?.get?.('facility_id');
  if (raw == null || String(raw).trim() === '') return null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? String(id) : null;
};

const readEncounterQueueParams = (search) => {
  const params = search instanceof URLSearchParams ? search : new URLSearchParams(search || '');
  const sort = parseSortParams(params, { sort_by: SORT_BY.CHECKIN_DATE, sort_dir: SORT_DIR.DESC });
  const typeRaw = params.get('type') || params.get('checkin_type_id') || '';
  return {
    facility_id: readFacilityIdParam(params) || '',
    from: ymdFromParam(params.get('from') || params.get('fromDate')),
    to: ymdFromParam(params.get('to') || params.get('toDate')),
    type: typeRaw ? String(typeRaw).split(',')[0] : '',
    status: params.get('status') ?? '',
    source: params.get('source') ?? '',
    insurance_no: params.get('insurance_no') || '',
    code: params.get('code') || '',
    sort_by: sort.sort_by,
    sort_dir: sort.sort_dir,
  };
};

const isEmptyEncounterQuery = (f) =>
  !f?.facility_id && !f?.from && !f?.to && !f?.type && (f?.status == null || f.status === '')
  && (f?.source == null || f.source === '') && !f?.insurance_no && !f?.code;

const isKeywordOnlyQuery = (f) =>
  Boolean(f?.insurance_no || f?.code)
  && !f?.facility_id && !f?.from && !f?.to && !f?.type
  && (f?.source == null || f.source === '')
  && (f?.status == null || f.status === '');

const isFacilityOnlyQuery = (f) =>
  Boolean(f?.facility_id)
  && !f?.from && !f?.to && !f?.type
  && (f?.source == null || f.source === '')
  && !f?.insurance_no && !f?.code;

const encounterParamsFromFilters = (filters, sort) => {
  const params = new URLSearchParams();
  const pairs = {
    facility_id: filters.facility_id,
    from: filters.from,
    to: filters.to,
    type: filters.type,
    status: filters.status,
    source: filters.source,
    insurance_no: filters.insurance_no,
    code: filters.code,
    sort_by: sort?.sort_by && sort.sort_by !== SORT_BY.CHECKIN_DATE ? sort.sort_by : '',
    sort_dir: sort?.sort_dir && sort.sort_dir !== SORT_DIR.DESC ? sort.sort_dir : '',
  };
  Object.entries(pairs).forEach(([key, value]) => {
    if (value == null || value === '') return;
    params.set(key, String(value));
  });
  return params;
};

const withPersistedFacility = (next = {}, params) => {
  const current = readEncounterQueueParams(params);
  const facilityId = next.facility_id ?? current.facility_id;
  const out = { ...next };
  if (facilityId) {
    out.facility_id = String(facilityId);
    if (out.status == null || out.status === '') {
      out.status = current.status || FACILITY_QUEUE_STATUS;
    }
  }
  if (current.from && out.from == null) out.from = current.from;
  if (current.to && out.to == null) out.to = current.to;
  if (current.type && out.type == null) out.type = current.type;
  if (current.source !== '' && out.source == null) out.source = current.source;
  return out;
};

const mapV2CheckinRow = (row) => {
  if (!row || typeof row !== 'object') return row;
  const patient = row.patient && typeof row.patient === 'object' ? row.patient : {};
  return {
    ...row,
    first_name: row.first_name || patient.first_name || '',
    surname: row.surname || patient.surname || '',
    middle_name: row.middle_name || patient.middle_name || '',
  };
};

const mapV2CheckinPagination = (pagination, fallbackPageSize = 10) => {
  const page = Number(pagination?.page) || 1;
  const pageSize = Number(pagination?.pageSize) || fallbackPageSize;
  const totalCount = Number(pagination?.total) || 0;
  const totalPages = Number(pagination?.totalPages) || Math.ceil(totalCount / Math.max(1, pageSize)) || 0;
  return {
    currentPage: page,
    pageSize,
    totalCount,
    totalPages,
    hasNextPage: page < totalPages,
    hasPrevPage: page > 1,
  };
};

const CheckinListHook = props => {
  const extended = props.extended !== undefined ? props.extended : true;

  const navigate = useNavigate();
  const { user } = useAuth();
  const { canStayOnModule, hasAnyPermissionWithLegacy } = usePermissions();
  const location = useLocation();
  const state = location?.state;
  const [searchParams, setSearchParams] = useSearchParams();
  const queueFilters = useMemo(() => readEncounterQueueParams(searchParams), [searchParams]);
  const urlFacilityId = queueFilters.facility_id || null;
  
  const [checkins, setCheckins] = useState([]);
  // PM parallel run: per-encounter unmapped-line counts (silent; null when the
  // viewer lacks plan_designer.manage or the pm subsystem is absent).
  const [pmUnmapped, setPmUnmapped] = useState(null);
  // Seeded from the URL so "Back to Queue" from the PA page lands on the same
  // filtered view (see queueReturnUrl below).
  const [pmUnmappedOnly, setPmUnmappedOnly] = useState(() => searchParams.get('pm_unmapped') === '1');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const codes = (checkins || []).map((c) => c?.code).filter(Boolean);
      if (!codes.length) { if (!cancelled) setPmUnmapped(null); return; }
      const res = await pmApi.unmappedCounts(codes);
      if (!cancelled) setPmUnmapped(res.ok ? (res.data?.counts || {}) : null);
    })();
    return () => { cancelled = true; };
  }, [checkins]);
  // After a mapping, re-count just that encounter so it drops out of the
  // "unmapped only" view on its own once nothing is left to map.
  const refreshPmUnmappedFor = useCallback(async (code) => {
    if (!code) return;
    const res = await pmApi.unmappedCounts([code]);
    if (!res.ok) return;
    const n = Number(res.data?.counts?.[code] || 0);
    setPmUnmapped((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      if (n > 0) next[code] = n; else delete next[code];
      return next;
    });
  }, []);
  const pmVisibleCheckins = useMemo(() => {
    if (!pmUnmappedOnly || !pmUnmapped) return checkins;
    return (checkins || []).filter((c) => Number(pmUnmapped[c?.code] || 0) > 0);
  }, [checkins, pmUnmapped, pmUnmappedOnly]);
  const [activeSearch, setActiveSearch] = useState(null);
  
  // Enhanced search state with better initialization
  const [searchType, setSearchType] = useState(() => (queueFilters.code ? 'encounter_code' : 'insurance_no'));
  const [searchValue, setSearchValue] = useState(() => queueFilters.insurance_no || queueFilters.code || '');
  const [searchHistory, setSearchHistory] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [viewMode, setViewMode] = useLocalStorage('encounters-view-mode-v2', 'table');
  const [tableDetailRecord, setTableDetailRecord] = useState(null);
  const [activeTab, setActiveTab] = useState('encounters');
  const [listSort, setListSort] = useState(() => ({
    sort_by: queueFilters.sort_by,
    sort_dir: queueFilters.sort_dir,
  }));
  const [facilityOption, setFacilityOption] = useState(() => (
    queueFilters.facility_id
      ? { value: queueFilters.facility_id, label: `Facility #${queueFilters.facility_id}` }
      : null
  ));
  const [checkinTypes, setCheckinTypes] = useState([]);
  const tableDetailsRef = useRef(null);

  const screens = Grid.useBreakpoint();
  const isCompactView = !screens.lg;
  const isMobileView = !screens.md;

  // Initialize search values from route state
  useEffect(() => {
    if (state?.filterKeyword?.dropdown) {
      setSearchType(state.filterKeyword.dropdown);
    }
    if (state?.item) {
      const initialValue = state.filterKeyword?.dropdown === 'encounter_code' 
        ? state.item.code || '' 
        : state.item.insurance_no || '';
      setSearchValue(initialValue);
    }
  }, [state]);

  
  const dispatcher = useDispatch(selectclient);
  const [loading, setLoading] = useState(false)

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const checkinFetchInterval = useRef();
  const checkinFetchTimeout = useRef();

  // Shows the encounters admin tab strip (Encounters / Checkout Validation / Cleanup).
  // Sits between the backend's read list and its restore-only list, which is right for a
  // strip that contains both.
  const isAdmin = hasAnyPermissionWithLegacy(
    ['admin.*', 'checkins.manage', 'encounters.manage'],
    [1, 3, 4, 5],
  );

  const isCleanupSuperAdmin = useMemo(() => isCheckinCleanupSuperAdmin(user), [user]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (!canStayOnModule('encounters', [1, 2, 3, 4, 5])) {
      notification.warning({
        message: 'Access Denied',
        description: "You don't have permission to view encounters.",
        placement: 'topLeft',
      });
      navigate('/dashboard');
    }
  }, [user, canStayOnModule, navigate]);

  useEffect(() => {
    let cancelled = false;
    lib.listAdminCheckinTypes({ status: 'active' })
      .then((resp) => {
        const parsed = lib.parseApiEnvelope(resp);
        const payload = parsed.data ?? parsed.body;
        const rows = Array.isArray(payload)
          ? payload
          : payload?.items || payload?.types || payload?.checkin_types || [];
        if (cancelled) return;
        const options = (rows || [])
          .map((t) => ({
            value: String(t.checkin_type_id ?? t.id ?? ''),
            label: t.checkin_type_name ?? t.name ?? t.label,
          }))
          .filter((t) => t.value && t.label);
        if (options.length) setCheckinTypes(options);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (state?.filterKeyword && isEmptyEncounterQuery(queueFilters)) {
      const dropdown = state.filterKeyword?.dropdown || 'insurance_no';
      const keyword = dropdown === 'encounter_code' ? state?.item?.code : state?.item?.insurance_no;
      if (keyword) {
        searchEncounters({ keyword, dropdown });
        return;
      }
    }
    // page / page_size only ever arrive here via queueReturnUrl (Back to Queue).
    const initialPage = Math.max(1, parseInt(searchParams.get('page'), 10) || 1);
    const initialPageSize = parseInt(searchParams.get('page_size'), 10) || undefined;
    loadEncountersFromFilters(queueFilters, { page: initialPage, pageSize: initialPageSize });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clearState = () => {
    navigate(location.pathname, { replace: true, state: null });
    setSearchParams({}, { replace: true });
  }

  const persistListSort = (next) => {
    setListSort(next);
    const params = encounterParamsFromFilters(queueFilters, next);
    setSearchParams(params, { replace: true });
    loadEncountersFromFilters(queueFilters, { page: 1, sort: next });
  };

  const withListSort = (payload = {}, sort = listSort) => ({
    ...payload,
    ...toSortQuery(sort.sort_by, sort.sort_dir),
  });

  const fetchCheckins = (url = 'aCheckinList', payload = {}, clear = true) => {
    if (clear) clearState();
    setLoading(true);

    return myRequestObj(url)
      .post(withListSort(payload))
      .then(res => res?.data)
      .then(data => {
        const dat = data?.data?.map(c => {
          const mapped = {};
          Object.entries(c).forEach(([key, value]) => {
            mapped[key] = value ?? '';
          });
          return mapped;
        }) || [];

        setCheckins(dat);
        
        if (data?.pagination) {
          setPagination(prev => ({
            ...prev,
            currentPage: data.pagination.currentPage || 1,
            pageSize: data.pagination.pageSize || 10,
            totalCount: data.pagination.totalCount || 0,
            totalPages: data.pagination.totalPages || 0,
            hasNextPage: data.pagination.hasNextPage || false,
            hasPrevPage: data.pagination.hasPrevPage || false,
          }));
        }

        return data;
      })
      .catch(error => {
        console.error('Failed to fetch encounters:', error);
      })
      .finally(() => {
        setLoading(false);
        setIsSearching(false);
      });
  };

  const clearIntervals = () => {
    clearTimeout(checkinFetchTimeout.current);
    clearInterval(checkinFetchInterval.current);
  }

  const applyCheckinRows = (rows, paginationBody, pageSize) => {
    const mapped = (rows || []).map(mapV2CheckinRow);
    setCheckins(mapped);
    if (paginationBody) {
      setPagination((prev) => ({ ...prev, ...mapV2CheckinPagination(paginationBody, pageSize) }));
    }
    const firstFacility = mapped.find((row) => row?.facility_name && row?.facility_id);
    if (firstFacility && String(firstFacility.facility_id) === String(queueFilters.facility_id || firstFacility.facility_id)) {
      setFacilityOption((prev) => {
        if (prev?.value && String(prev.value) !== String(firstFacility.facility_id)) return prev;
        const label = firstFacility.facility_name || prev?.label;
        if (prev?.label === label && String(prev?.value) === String(firstFacility.facility_id)) return prev;
        return { value: String(firstFacility.facility_id), label };
      });
    }
    return mapped;
  };

  const loadFacilityEncounters = (facilityId, extra = {}) => {
    const id = Number(facilityId);
    if (!Number.isFinite(id) || id <= 0) return Promise.resolve();

    setActiveSearch(null);
    setLoading(true);

    const page = extra.page || 1;
    const pageSize = extra.pageSize || pagination.pageSize || 10;
    const statusRaw = extra.status == null || extra.status === '' ? '0' : String(extra.status);
    // New flow (spec §7, §8): "claimable" / "pending" are list scopes on
    // /v2/checkins rather than checkin_status values.
    const newFlowScope = NEW_FLOW_STATUS_SCOPES[statusRaw] || null;
    const status = newFlowScope ? null : Number(statusRaw);
    if (extra.writeUrl) {
      setSearchParams(
        encounterParamsFromFilters(
          { ...queueFilters, facility_id: String(id), status: statusRaw },
          extra.sort || listSort,
        ),
        { replace: true },
      );
    }

    const query = newFlowScope
      ? {
          facility_id: id,
          page,
          pageSize,
          ...newFlowScope,
          ...(extra.from ? { date_from: extra.from } : {}),
          ...(extra.to ? { date_to: extra.to } : {}),
          ...(extra.type ? { checkin_type: extra.type } : {}),
        }
      : { facility_id: id, status, page, pageSize };

    return lib.getCheckinsV2(withListSort(query, extra.sort))
      .then((resp) => {
        const parsed = lib.parseApiEnvelope(resp);
        const body = parsed.body || {};
        if (!parsed.ok) {
          setCheckins([]);
          if (resp?.status === 'error' && parsed.message) {
            notification.error({
              message: parsed.message || 'Failed to load facility encounters',
              placement: 'topLeft',
            });
          }
          return parsed;
        }
        const rows = Array.isArray(parsed.data) ? parsed.data : [];
        applyCheckinRows(rows, body.pagination, pageSize);
        return parsed;
      })
      .catch((error) => {
        console.error('Failed to fetch facility encounters:', error);
        setCheckins([]);
      })
      .finally(() => {
        setLoading(false);
        setIsSearching(false);
      });
  };

  const loadAdminLiveCheckins = (filters, extra = {}) => {
    setLoading(true);
    const page = extra.page || 1;
    const pageSize = extra.pageSize || pagination.pageSize || 10;
    const params = withListSort({ deleted: 0, page, pageSize }, extra.sort);
    if (filters.facility_id) params.facility_id = filters.facility_id;
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;
    if (filters.type) params.type = filters.type;
    if (filters.status !== '' && filters.status != null) params.status = filters.status;
    if (filters.source !== '' && filters.source != null) params.source = filters.source;
    // insurance_no/code hold the free-typed search box value here (same as the
    // keyword-only path above), so send it as a fuzzy `search` match rather than
    // an exact-equality filter — otherwise typing a partial value while any other
    // filter (facility/date/type/status/source) is active returns nothing.
    if (filters.insurance_no || filters.code) params.search = filters.insurance_no || filters.code;

    return lib.getAdminCheckins(params)
      .then((resp) => {
        const parsed = lib.parseApiEnvelope(resp);
        const body = parsed.body || {};
        if (!parsed.ok) {
          setCheckins([]);
          notification.error({
            message: parsed.message || 'Failed to load encounters',
            placement: 'topLeft',
          });
          return parsed;
        }
        const rows = Array.isArray(parsed.data) ? parsed.data : [];
        applyCheckinRows(rows, body.pagination, pageSize);
        return parsed;
      })
      .catch((error) => {
        console.error('Failed to fetch admin encounters:', error);
        setCheckins([]);
      })
      .finally(() => {
        setLoading(false);
        setIsSearching(false);
      });
  };

  const loadEncountersFromFilters = (filters, extra = {}) => {
    const page = extra.page || 1;
    const pageSize = extra.pageSize || pagination.pageSize || 10;
    const sort = extra.sort || listSort;

    if (isEmptyEncounterQuery(filters)) {
      setActiveSearch(null);
      return fetchCheckins('aCheckinList', { status: 0, source: 1, target: 'manual', page, pageSize }, false);
    }

    if (isKeywordOnlyQuery(filters)) {
      const keyword = filters.insurance_no || filters.code;
      const dropdown = filters.code ? 'encounter_code' : 'insurance_no';
      setSearchType(dropdown);
      setSearchValue(keyword);
      setActiveSearch({ keyword, dropdown });
      return fetchCheckins('encounters', { keyword, target: dropdown, page, pageSize }, false);
    }

    if (filters.facility_id && NEW_FLOW_STATUS_SCOPES[filters.status]) {
      return loadFacilityEncounters(filters.facility_id, {
        page,
        pageSize,
        status: filters.status,
        from: filters.from,
        to: filters.to,
        type: filters.type,
        sort,
        writeUrl: false,
      });
    }

    if (isFacilityOnlyQuery(filters)) {
      return loadFacilityEncounters(filters.facility_id, {
        page,
        pageSize,
        status: filters.status,
        sort,
        writeUrl: false,
      });
    }

    if (filters.insurance_no || filters.code) {
      const keyword = filters.insurance_no || filters.code;
      const dropdown = filters.code ? 'encounter_code' : 'insurance_no';
      setSearchType(dropdown);
      setSearchValue(keyword);
      setActiveSearch({ keyword, dropdown });
    } else {
      setActiveSearch(null);
    }

    return loadAdminLiveCheckins(filters, { page, pageSize, sort });
  };

  const writeEncounterParams = (patch, extra = {}) => {
    const next = { ...queueFilters, ...patch };
    if (patch.facility_id && (next.status == null || next.status === '')) {
      next.status = FACILITY_QUEUE_STATUS;
    }
    setSearchParams(encounterParamsFromFilters(next, extra.sort || listSort), { replace: true });
    loadEncountersFromFilters(next, { page: extra.page || 1, pageSize: extra.pageSize, sort: extra.sort });
  };

  const handlePageChange = (page, pageSize) => {
    loadEncountersFromFilters(queueFilters, { page, pageSize });
  };

  const searchEncounters = ({ keyword, dropdown: dropdownArg }) => {
    // The dropdown ("search by") value can arrive empty if the child search
    // component's own state ever desyncs — fall back to what we already know
    // the user last had selected instead of silently dropping the search.
    const dropdown = dropdownArg || searchType;

    if (!dropdown && !keyword) {
      notification.error({ message: "What are you trying to search?" })
      return;
    }

    if (!dropdown) {
      notification.error({ message: "Search option must be delected!" })
      return;
    }

    if (!keyword) {
      notification.error({ message: "What do you want to search?" })
      return;
    }

    setSearchType(dropdown);
    setSearchValue(keyword);
    setActiveSearch({ keyword, dropdown });

    const urlParam = dropdown === 'encounter_code' ? 'code' : 'insurance_no';
    const next = withPersistedFacility({
      ...queueFilters,
      insurance_no: urlParam === 'insurance_no' ? keyword : '',
      code: urlParam === 'code' ? keyword : '',
    }, searchParams);
    writeEncounterParams(next);
  }

  const handleSearch = (value = searchValue) => {
    const trimmedValue = value?.trim();
    
    if (!searchType) {
      notification.error({ 
        message: "Search Type Required", 
        description: "Please select a search type first."
      });
      return;
    }

    if (!trimmedValue) {
      notification.error({ 
        message: "Search Value Required", 
        description: "Please enter a search value."
      });
      return;
    }

    if (isSearching) {
      notification.warning({ 
        message: "Search in Progress", 
        description: "Please wait for the current search to complete."
      });
      return;
    }

    const searchEntry = { type: searchType, value: trimmedValue, timestamp: Date.now() };
    setSearchHistory(prev => {
      const filtered = prev.filter(item => 
        !(item.type === searchType && item.value === trimmedValue)
      );
      return [searchEntry, ...filtered].slice(0, 5);
    });

    setIsSearching(true);
    searchEncounters({ keyword: trimmedValue, dropdown: searchType });
  };

  const handleClearSearch = () => {
    setSearchValue('');
    setActiveSearch(null);
    setFacilityOption(null);
    navigate(location.pathname, { replace: true, state: null });
    setSearchParams({}, { replace: true });
    loadEncountersFromFilters({
      facility_id: '',
      from: '',
      to: '',
      type: '',
      status: '',
      source: '',
      insurance_no: '',
      code: '',
    }, { page: 1 });
  };

  const handleSearchTypeChange = (newType) => {
    setSearchType(newType);
    setSearchValue('');
  };

  const loadAtiveManualEncounter = () => {
    setActiveSearch(null);
    return fetchCheckins("aCheckinList", { status: 0, source: 1, target: 'manual' }, false);
  };

  const handleFacilitySearch = async (q) => {
    if (!q || String(q).length < 3) return [];
    try {
      const resp = await lib.unifiedSearch({
        resource: 'facilities',
        search: q,
        page: 1,
        pageSize: 50,
      });
      const body = resp?.data;
      if (body && Number(body?.error) === 0) {
        return (body.data || []).map((loc) => ({
          label: loc.name || loc.facility_name,
          value: String(loc.id || loc.facility_id),
        }));
      }
    } catch {
      /* ignore */
    }
    return [];
  };

  const checkout = async (record, skipConfirmation = false) => {
    // Show confirmation modal first unless explicitly skipped
    if (!skipConfirmation) {
      // Fetch validation status for display
      let validation = null;
      let validationCode = null;
      try {
        const validationResp = await lib.getCheckoutValidation(record?.code);
        const validationBody = validationResp?.data;
        validation = validationBody?.data || validationBody;
        // The verdict code lives on the envelope, not inside `data`.
        validationCode = validationBody?.code ?? validation?.code ?? null;
      } catch (error) {
        console.error('Error checking validation:', error);
        // Continue without validation info
      }

      // Every PA line was rejected: checkout is still allowed — it only
      // releases the client, and the encounter is NOT deleted. No claim can be
      // filed for it as it stands; the facility can add new PA items and
      // submit when ready.
      const allPaRejected = validationCode === ALL_PA_REJECTED_CODE;
      // No items at all: checking out removes the encounter (restorable by an admin).
      const noItems = validationCode === 'NO_PA_ITEMS';

      // If validation fails or checkout is not allowed, show error
      if (validation && !validation.can_checkout) {
        notification.error({
          message: 'Checkout Not Allowed',
          description: validation.warnings?.length > 0 
            ? validation.warnings.join(', ')
            : 'This encounter cannot be checked out at this time.',
          duration: 5,
        });
        return;
      }

      // Show confirmation modal
      Modal.confirm({
        title: (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {allPaRejected ? (
              <WarningOutlined style={{ fontSize: '24px', color: '#faad14' }} />
            ) : (
              <CheckCircleOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
            )}
            <span style={{ fontSize: '18px', fontWeight: 600 }}>
              {allPaRejected
                ? 'Confirm checkout — every PA item was rejected'
                : noItems
                  ? 'Confirm checkout — this encounter has no items'
                  : 'Confirm Checkout'}
            </span>
          </div>
        ),
        width: 600,
        content: (
          <div style={{ marginTop: '16px' }}>
            {/* Encounter Details */}
            <Card 
              size="small" 
              style={{ marginBottom: '16px', backgroundColor: '#f5f7fa' }}
              bodyStyle={{ padding: '12px' }}
            >
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography.Text strong>Patient:</Typography.Text>
                  <Typography.Text>
                    {`${record?.surname?.toUpperCase?.() || ''}${record?.surname ? ',' : ''} ${record?.first_name || ''} ${record?.middle_name || ''}`.trim() || 'N/A'}
                  </Typography.Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography.Text strong>Insurance No:</Typography.Text>
                  <Typography.Text>{record?.insurance_no || 'N/A'}</Typography.Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography.Text strong>Check-in code:</Typography.Text>
                  <Typography.Text code>{record?.code || 'N/A'}</Typography.Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography.Text strong>Facility:</Typography.Text>
                  <Typography.Text>{record?.facility_name || 'N/A'}</Typography.Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography.Text strong>Check-In Date:</Typography.Text>
                  <Typography.Text>{formatDateTime(record?.checkin_date) || 'N/A'}</Typography.Text>
                </div>
              </Space>
            </Card>

            {/* Validation Status */}
            {validation && (
              <div style={{ marginBottom: '16px' }}>
                <Alert
                  type={validation.can_checkout ? 'success' : 'warning'}
                  message={
                    <Space>
                      <span>Validation Status:</span>
                      <Tag color={validation.can_checkout ? 'green' : 'orange'}>
                        {validation.validation_status || (validation.can_checkout ? 'Valid' : 'Invalid')}
                      </Tag>
                    </Space>
                  }
                  description={
                    validation.pa_status ? (
                      <div style={{ marginTop: '8px' }}>
                        <Row gutter={[8, 8]}>
                          <Col span={6}>
                            <Statistic 
                              title="Total Items" 
                              value={validation.pa_status.total_items || 0} 
                              valueStyle={{ fontSize: '14px' }}
                            />
                          </Col>
                          <Col span={6}>
                            <Statistic 
                              title="Approved" 
                              value={validation.pa_status.approved_items || 0} 
                              valueStyle={{ fontSize: '14px', color: '#52c41a' }}
                            />
                          </Col>
                          <Col span={6}>
                            <Statistic 
                              title="Pending" 
                              value={validation.pa_status.pending_items || 0} 
                              valueStyle={{ fontSize: '14px', color: '#faad14' }}
                            />
                          </Col>
                          <Col span={6}>
                            <Statistic 
                              title="Rejected" 
                              value={validation.pa_status.rejected_items || 0} 
                              valueStyle={{ fontSize: '14px', color: '#ff4d4f' }}
                            />
                          </Col>
                        </Row>
                      </div>
                    ) : null
                  }
                  showIcon
                />
              </div>
            )}

            {/* Evidence status */}
            {validation && formatCheckoutEvidenceSummary(validation) && (
              <Alert
                type={validation.can_checkout ? 'info' : 'warning'}
                message="PA evidence"
                description={formatCheckoutEvidenceSummary(validation)}
                showIcon
                style={{ marginBottom: '16px' }}
              />
            )}

            {/* What checking out actually does (revised flow). */}
            {allPaRejected ? (
              <Alert
                type="warning"
                message="Nothing to approve — no claim can be filed"
                description="Every PA item on this encounter was rejected, so there is no claim to submit. Checkout only releases the client: the encounter stays on the Encounters page, the rejected items stay on record, and the facility can add new PA items and submit a claim when ready."
                showIcon
                style={{ marginBottom: '16px' }}
              />
            ) : noItems ? (
              <Alert
                type="error"
                message="Checkout removes this encounter"
                description="This encounter has no items, so checking it out removes it from the Encounters page. To keep it, cancel and add its items first. It can be restored later from the deleted check-ins list."
                showIcon
                style={{ marginBottom: '16px' }}
              />
            ) : null}

            {/* Warnings */}
            {validation && validation.warnings && validation.warnings.length > 0 && (
              <Alert
                type="warning"
                message="Warnings"
                description={
                  <ul style={{ marginTop: '8px', marginBottom: 0, paddingLeft: '20px' }}>
                    {validation.warnings.map((warning, idx) => (
                      <li key={idx} style={{ marginBottom: '4px' }}>{warning}</li>
                    ))}
                  </ul>
                }
                showIcon
                style={{ marginBottom: '16px' }}
              />
            )}

            {/* Confirmation Message. */}
            <div style={{
              padding: '12px',
              backgroundColor: '#e6f7ff',
              borderRadius: '4px',
              border: '1px solid #91d5ff'
            }}>
              <Typography.Text>
                {noItems
                  ? 'Checkout releases the client\'s active check-in status and removes this encounter, because it has no items.'
                  : 'Checkout only releases the client\'s active check-in status. The encounter stays on the Encounters page, fully editable, and its claim is submitted separately.'}
              </Typography.Text>
            </div>
          </div>
        ),
        okText: noItems ? 'Check out and remove' : 'Yes, Checkout',
        cancelText: 'Cancel',
        okButtonProps: {
          type: 'primary',
          size: 'large',
          style: { minWidth: '120px' }
        },
        cancelButtonProps: { 
          size: 'large',
          style: { minWidth: '120px' }
        },
        onOk: () => checkout(record, true), // Retry with skipConfirmation = true
        style: { top: 100 },
      });
      return;
    }
    
    // Proceed with checkout
    setLoading(true);
    const payload = { checkinId: record?.code };
    lib.checkout(payload)
      .then(resp => resp?.data)
      .then(data => {
        notification.success({
          message: data?.message || 'Checkout completed — the encounter stays on the Encounters page.',
        });
        if (!Number(data?.error)) refreshEncounters();
      })
      .catch(error => {
        console.error('Checkout error:', error);
        notification.error({
          message: 'Checkout Failed',
          description: error?.response?.data?.message || error?.message || 'Failed to complete checkout',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const refreshEncounters = () => {
    loadEncountersFromFilters(queueFilters, {
      page: pagination.currentPage,
      pageSize: pagination.pageSize,
    });
  };

  // --- New flow (spec §7, §8): submit claims on a facility's behalf --------------
  // Every path goes through the normal server-side submission (one encounter at
  // a time, same checks, same audit); none of them creates a new claim batch —
  // each claim stays in its encounter month.
  const [selectedEncounterCodes, setSelectedEncounterCodes] = useState([]);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  useEffect(() => {
    setSelectedEncounterCodes([]);
  }, [searchParams]);

  const showBulkResult = (title, data) => {
    const results = Array.isArray(data?.results) ? data.results : [];
    const failed = results.filter((r) => !r.ok);
    Modal[failed.length ? 'warning' : 'success']({
      title,
      width: 640,
      content: (
        <div>
          <p>
            {data?.submitted ?? 0} of {data?.total ?? results.length} claim(s) submitted.
            {data?.truncated
              ? ` This request was capped at ${data?.max_batch || 200}; run it again for the rest.`
              : ''}
          </p>
          {failed.length ? (
            <ul style={{ maxHeight: 240, overflow: 'auto', paddingLeft: 18 }}>
              {failed.map((r) => (
                <li key={r.checkin_id}>
                  <strong>{r.checkin_id}</strong>: {r.message}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ),
    });
  };

  const submitClaimOnBehalf = (record) => {
    if (!record?.code) return;
    Modal.confirm({
      title: 'Submit this claim?',
      content: (
        <div>
          <p>
            Submits the claim for <strong>{record.code}</strong>
            {record.facility_name ? <> on behalf of <strong>{record.facility_name}</strong></> : null}.
          </p>
          <p style={{ marginBottom: 0 }}>
            It goes through the normal claim submission and stays in its encounter month.
          </p>
        </div>
      ),
      okText: SUBMIT_CLAIM_LABEL,
      onOk: async () => {
        const resp = await lib.submitClaimV2(record.code, {});
        const parsed = lib.parseApiEnvelope(resp);
        if (!parsed.ok) {
          notification.error({ message: 'Claim not submitted', description: parsed.message });
          return;
        }
        notification.success({ message: 'Claim submitted', description: record.code });
        refreshEncounters();
      },
    });
  };

  const submitSelectedClaims = () => {
    const codes = selectedEncounterCodes.slice();
    if (!codes.length) return;
    Modal.confirm({
      title: `Submit ${codes.length} selected claim${codes.length === 1 ? '' : 's'}?`,
      content: 'Each encounter is submitted through the normal claim submission and stays in its encounter month.',
      okText: `Submit ${codes.length}`,
      onOk: async () => {
        setBulkSubmitting(true);
        try {
          const resp = await lib.submitPendingClaims({ checkin_ids: codes });
          const parsed = lib.parseApiEnvelope(resp);
          if (!parsed.ok) {
            notification.error({ message: 'Submission failed', description: parsed.message });
            return;
          }
          setSelectedEncounterCodes([]);
          showBulkResult('Selected claims', parsed.data);
          refreshEncounters();
        } finally {
          setBulkSubmitting(false);
        }
      },
    });
  };

  // Submit All (spec §7, §8): every eligible pending claim for the selected
  // facility under the current filters. No facility-style review.
  const submitAllForFacility = () => {
    const facilityId = Number(queueFilters.facility_id);
    if (!facilityId) return;
    const previousOnly = queueFilters.status === 'pending';
    Modal.confirm({
      title: 'Submit all pending claims for this facility?',
      content: (
        <div>
          <p>
            Submits every eligible pending claim for this facility
            {queueFilters.from || queueFilters.to ? ' within the selected dates' : ''}
            {queueFilters.type ? ' of the selected check-in type' : ''}
            {previousOnly ? ', from previous months only' : ''}.
          </p>
          <p style={{ marginBottom: 0 }}>
            Each claim stays in its encounter month. Up to 200 are submitted per run.
          </p>
        </div>
      ),
      okText: 'Submit all',
      onOk: async () => {
        setBulkSubmitting(true);
        try {
          const resp = await lib.submitPendingClaims({
            facility_id: facilityId,
            from: queueFilters.from || undefined,
            to: queueFilters.to || undefined,
            type: queueFilters.type || undefined,
            previous_months_only: previousOnly ? 1 : 0,
          });
          const parsed = lib.parseApiEnvelope(resp);
          if (!parsed.ok) {
            notification.error({ message: 'Submission failed', description: parsed.message });
            return;
          }
          showBulkResult('Submit all', parsed.data);
          refreshEncounters();
        } finally {
          setBulkSubmitting(false);
        }
      },
    });
  };

  // Table / card / modal all call this: Submit Claim in the new flow, the
  // existing checkout otherwise.
  const encounterPrimaryAction = (record, ...rest) =>
    submitMode ? submitClaimOnBehalf(record) : checkout(record, ...rest);

  const heroMetrics = useMemo(() => {
    const total = pagination.totalCount || checkins.length;
    const active = checkins.filter((item) => Number(item?.checkin_status) === 0).length;
    const manual = checkins.filter((item) => Number(item?.source) === 1).length;
    const inPatients = checkins.filter((item) => Number(item?.checkinType) === 1).length;
    const facilities = new Set(checkins.map((item) => item?.facility_name).filter(Boolean)).size;

    return [
      {
        key: 'total',
        title: 'Total encounters',
        value: total,
        color: '#3497D3',
        icon: <UnorderedListOutlined />,
        subtitle: active ? `${formatNumber(active)} active on this page` : 'Matching current view',
      },
      {
        key: 'manual',
        title: 'Manual filings',
        value: manual,
        color: '#49B870',
        icon: <FileTextOutlined />,
        subtitle: manual ? `${formatNumber(checkins.length - manual)} facility filed` : 'On this page',
      },
      {
        key: 'inpatients',
        title: 'In-patient',
        value: inPatients,
        color: '#faad14',
        icon: <MedicineBoxOutlined />,
        subtitle: inPatients ? `${formatNumber(checkins.length - inPatients)} out-patient` : 'On this page',
      },
      {
        key: 'facilities',
        title: 'Facilities',
        value: facilities,
        color: '#722ed1',
        icon: <CheckCircleOutlined />,
        subtitle: facilities ? 'Represented on this page' : 'No facility data yet',
      },
    ];
  }, [checkins, pagination.totalCount]);

  const isLoadingState = loading && !checkins.length;

  // Row actions on the encounters queue: "Open PA" navigates into the preauth module,
  // "Checkout" closes an open encounter.
  const canViewPA = hasAnyPermissionWithLegacy(
    ['preauth.read', 'preauth.decide', 'preauth.approve'],
    [1, 2, 3],
  );
  // Mirrors LegacyAdminActionGate::ACTION_CHECKOUT_ENCOUNTER exactly. Checking
  // out closes the encounter AND files its claim, so it now has a permission
  // of its own instead of borrowing checkins.update — "may edit a check-in"
  // should never have implied "may file a claim". The coarser lifecycle
  // permissions stay accepted so nobody loses the action before the backfill
  // migration runs.
  const canCheckoutEncounter = hasAnyPermissionWithLegacy(
    ENCOUNTER_CHECKOUT_PERMISSIONS,
    [1, 3],
  );
  // EncountersQueueTable, EncounterDetailsModal and EncounterCard still derive those two
  // actions from `[1, 2, 3].includes(userRoleId)` and `[1, 3].includes(userRoleId)`.
  // Those files are migrated separately, so keep the prop contract and hand them a
  // permission-derived stand-in instead of the real legacy role id:
  //   1 -> open PA + checkout, 2 -> open PA only, 0 -> neither.
  // New flow (spec §7): Check Out becomes Submit Claim, which authorised
  // admin/claims users may do on a facility's behalf. Off = unchanged.
  const claimsRollout = useClaimsRollout();
  const submitMode = Boolean(claimsRollout.submit_separation_enabled);
  const canSubmitOnBehalf = hasAnyPermissionWithLegacy(
    ['claims.process', 'claims.create', 'encounters.checkout', 'admin.*'],
    [1, 3],
  );
  const userRoleId = submitMode
    ? (canSubmitOnBehalf ? 1 : canViewPA ? 2 : 0)
    : (canViewPA && canCheckoutEncounter ? 1 : canViewPA ? 2 : 0);

  // "File encounter" navigates to /file-encounter, whose Send posts to
  // legacy.php?claimLog. That endpoint is now gated, so matching the
  // ProtectedRoute's preauth module is no longer enough — preauth.read alone
  // would open the screen, let the user compose a whole claim, and then 403 on
  // Send. Mirror the endpoint instead.
  const canFileEncounter = hasAnyPermissionWithLegacy(
    ENCOUNTER_FILE_CLAIM_PERMISSIONS,
    LEGACY_FILE_CLAIM_ROLES,
  );
  const dateRangeValue = queueFilters.from && queueFilters.to
    ? [dayjs(queueFilters.from), dayjs(queueFilters.to)]
    : queueFilters.from
      ? [dayjs(queueFilters.from), null]
      : null;
  const hasListFilters = !isEmptyEncounterQuery(queueFilters);

  const openTableDetails = useCallback((record) => {
    setTableDetailRecord(record);
    requestAnimationFrame(() => tableDetailsRef.current?.open());
  }, []);

  const handleTypeChanged = useCallback((next) => {
    if (!next?.code) return;
    setCheckins((prev) =>
      prev.map((row) => (row?.code === next.code ? { ...row, ...next } : row)),
    );
    setTableDetailRecord((prev) =>
      prev?.code === next.code ? { ...prev, ...next } : prev,
    );
  }, []);

  // The queue's own URL plus the bits that live only in state (page, page size,
  // unmapped-only toggle), so the PA page's "Back to Queue" can rebuild this
  // exact view — the same approach as the Facilities directory.
  const queueReturnUrl = useMemo(() => {
    const params = encounterParamsFromFilters(queueFilters, listSort);
    if (pagination.currentPage > 1) params.set('page', String(pagination.currentPage));
    if (pagination.pageSize && pagination.pageSize !== 10) params.set('page_size', String(pagination.pageSize));
    if (pmUnmappedOnly) params.set('pm_unmapped', '1');
    const qs = params.toString();
    return qs ? `${location.pathname}?${qs}` : location.pathname;
  }, [queueFilters, listSort, pagination.currentPage, pagination.pageSize, pmUnmappedOnly, location.pathname]);

  const handleViewPA = useCallback((item) => {
    navigate(`/preauth-action?checkin=${encodeURIComponent(item?.code || '')}`, {
      state: { item, filterKeyword: activeSearch, queueUrl: queueReturnUrl },
    });
  }, [navigate, activeSearch, queueReturnUrl]);

  // Admin tabs for checkout validation (+ cleanup for super admin only)
  const adminTabs = isAdmin ? [
    {
      key: 'encounters',
      label: 'Encounters',
    },
    {
      key: 'validation',
      label: 'Checkout Validation',
    },
    ...(isCleanupSuperAdmin ? [{
      key: 'cleanup',
      label: 'Check-in cleanup',
    }] : []),
  ] : null;

  return (
    <div className="encounters-dashboard">
      {extended && (
        <div className="dashboard-page-stack">
          <header className="dash-masthead">
            <div className="dash-masthead__lead">
              <h1 className="dash-masthead__title">Encounters</h1>
              <p className="dash-masthead__meta">
                Search, review, and manage patient check-ins
              </p>
            </div>
          </header>
          <ModuleNav items={MODULE_NAVS.encounters} />

          {isAdmin && adminTabs && (
            <div className="encounters-admin-panel">
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={adminTabs.map((tab) => ({
                  ...tab,
                  children:
                    tab.key === 'validation' ? (
                      <div className="encounters-admin-panel__body">
                        <AdminCheckoutValidationChecker />
                      </div>
                    ) : tab.key === 'cleanup' ? (
                      <div className="encounters-admin-panel__body">
                        <CheckinCleanupPanel />
                      </div>
                    ) : null,
                }))}
              />
            </div>
          )}

          {(!isAdmin || activeTab === 'encounters') && (
            <>
              <section className="encounters-overview dash-overview dash-overview--compact" aria-label="Encounters overview">
                <DashboardHero
                  metrics={heroMetrics}
                  loading={loading && !checkins.length && !pagination.totalCount}
                />
              </section>

              <DashboardSection
                id="encounters-queue"
                icon={<MedicineBoxOutlined />}
                title="Encounter queue"
                subtitle={
                  hasListFilters
                    ? 'Filtered by the bar below — Reset returns to the manual PA queue'
                    : viewMode === 'table'
                      ? 'Manual PA queue · click a row for patient details'
                      : 'Manual PA queue · click a card for patient details'
                }
                collapsible={false}
                variant="content"
              >
                <div className="encounters-queue-filters">
                  <div className="encounters-queue-filters__field encounters-queue-filters__field--grow">
                    <span className="encounters-queue-filters__label">Facility</span>
                    <DebounceSelect
                      allowClear
                      showSearch
                      labelInValue
                      placeholder="Filter by facility"
                      value={facilityOption}
                      fetchOptions={handleFacilitySearch}
                      onChange={(option) => {
                        setFacilityOption(option || null);
                        writeEncounterParams({ facility_id: option?.value ? String(option.value) : '' });
                      }}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div className="encounters-queue-filters__field">
                    <span className="encounters-queue-filters__label">Check-in date</span>
                    <DatePicker.RangePicker
                      allowClear
                      value={dateRangeValue}
                      onChange={(vals) => {
                        writeEncounterParams({
                          from: vals?.[0] ? vals[0].format('YYYY-MM-DD') : '',
                          to: vals?.[1] ? vals[1].format('YYYY-MM-DD') : '',
                        });
                      }}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div className="encounters-queue-filters__field">
                    <span className="encounters-queue-filters__label">Type</span>
                    <Select
                      allowClear
                      placeholder="All types"
                      value={queueFilters.type || undefined}
                      options={checkinTypes.length ? checkinTypes : CHECKIN_TYPE_FALLBACK}
                      onChange={(value) => writeEncounterParams({ type: value || '' })}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div className="encounters-queue-filters__field">
                    <span className="encounters-queue-filters__label">Status</span>
                    <Select
                      allowClear
                      placeholder="All statuses"
                      value={queueFilters.status === '' ? undefined : queueFilters.status}
                      options={submitMode ? [...STATUS_OPTIONS, ...NEW_FLOW_STATUS_OPTIONS] : STATUS_OPTIONS}
                      onChange={(value) => writeEncounterParams({ status: value ?? '' })}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div className="encounters-queue-filters__field">
                    <span className="encounters-queue-filters__label">Source</span>
                    <Select
                      allowClear
                      placeholder="All sources"
                      value={queueFilters.source === '' ? undefined : queueFilters.source}
                      options={SOURCE_OPTIONS}
                      onChange={(value) => writeEncounterParams({ source: value ?? '' })}
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>

                <div className="encounters-toolbar">
                  <div className="encounters-toolbar__search">
                    <SearchWithAdvancedFeatures
                      dropdownOptions={[
                        { value: 'insurance_no', label: 'Insurance No' },
                        { value: 'encounter_code', label: 'Check-in code' },
                      ]}
                      loading={loading || isSearching}
                      onSearch={searchEncounters}
                      showDropdown
                      allowClear
                      customButtonLabel="Search"
                      defaultDropdownValue={queueFilters.code ? 'encounter_code' : 'insurance_no'}
                      defaultSearchValue={queueFilters.code || queueFilters.insurance_no || searchValue}
                      customStyles={{
                        container: { width: '100%' },
                        select: { width: '100%', borderRadius: 10 },
                        search: { width: '100%', borderRadius: 10 },
                      }}
                    />
                  </div>
                  {canFileEncounter && (
                    <Button type="primary" size="small" onClick={() => navigate('/file-encounter')}>
                      File encounter
                    </Button>
                  )}
                  {submitMode && canSubmitOnBehalf && selectedEncounterCodes.length > 0 ? (
                    <Button
                      size="small"
                      type="primary"
                      loading={bulkSubmitting}
                      onClick={submitSelectedClaims}
                    >
                      Submit selected ({selectedEncounterCodes.length})
                    </Button>
                  ) : null}
                  {submitMode && canSubmitOnBehalf && queueFilters.facility_id ? (
                    <Button size="small" loading={bulkSubmitting} onClick={submitAllForFacility}>
                      Submit All
                    </Button>
                  ) : null}
                  <Button size="small" onClick={handleClearSearch} disabled={loading}>
                    Reset
                  </Button>
                  {checkins.length > 0 || loading ? (
                    <Segmented
                      className="encounters-view-toggle"
                      size="small"
                      value={viewMode}
                      onChange={setViewMode}
                      options={[
                        { label: isMobileView ? '' : 'Cards', value: 'grid', icon: <AppstoreOutlined /> },
                        { label: isMobileView ? '' : 'Table', value: 'table', icon: <TableOutlined /> },
                      ]}
                    />
                  ) : null}
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    onClick={refreshEncounters}
                    loading={loading}
                  >
                    Refresh
                  </Button>
                </div>

                {isLoadingState ? (
                  viewMode === 'table' ? (
                    <TableSkeleton rows={8} columns={6} />
                  ) : (
                    <SearchResultsSkeleton count={6} />
                  )
                ) : checkins.length === 0 ? (
                  <div className="encounters-empty">
                    <MedicineBoxOutlined style={{ fontSize: 32, color: '#94a3b8' }} />
                    <p className="encounters-empty__title">No encounters found</p>
                    <p className="encounters-empty__desc">
                      {activeSearch
                        ? 'Try a different insurance number or Check-in code.'
                        : urlFacilityId
                          ? 'No encounters for this facility with the current filters.'
                          : 'Encounters matching your filters will appear here.'}
                    </p>
                  </div>
                ) : viewMode === 'table' ? (
                  <>
                    {pmUnmapped && (Object.keys(pmUnmapped).length || pmUnmappedOnly) ? (
                      <div style={{ margin: '4px 0 8px' }}>
                        <Checkbox checked={pmUnmappedOnly} onChange={(e) => setPmUnmappedOnly(e.target.checked)}>
                          Encounters with unmapped items only ({Object.keys(pmUnmapped).length} on this page)
                        </Checkbox>
                        {pmUnmappedOnly && pmVisibleCheckins.length === 0 ? (
                          <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                            Every encounter on this page is mapped.
                          </Typography.Text>
                        ) : null}
                      </div>
                    ) : null}
                    <EncountersQueueTable
                      data={pmVisibleCheckins}
                      userRoleId={userRoleId}
                      onOpenDetails={openTableDetails}
                      onViewPA={handleViewPA}
                      onCheckout={encounterPrimaryAction}
                      isCheckoutLoading={loading}
                      sortBy={listSort.sort_by}
                      sortDir={listSort.sort_dir}
                      onSortChange={persistListSort}
                      pmUnmapped={pmUnmapped}
                      submitMode={submitMode}
                      selectedRowKeys={selectedEncounterCodes}
                      onSelectionChange={canSubmitOnBehalf ? setSelectedEncounterCodes : undefined}
                    />
                    <EncounterDetailsModal
                      ref={tableDetailsRef}
                      data={tableDetailRecord || {}}
                      userRoleId={userRoleId}
                      onViewPA={handleViewPA}
                      onCheckout={encounterPrimaryAction}
                      submitMode={submitMode}
                      onTypeChanged={handleTypeChanged}
                      onPmMapped={refreshPmUnmappedFor}
                      isCheckoutLoading={loading}
                    />
                  </>
                ) : (
                  <div className="encounters-cards-grid encounters-cards-grid--linear">
                    {pmVisibleCheckins.map((item) => (
                      <EncounterCard
                        key={item?.code || item?.insurance_no}
                        item={item}
                        userRoleId={userRoleId}
                        onViewPA={() => handleViewPA(item)}
                        onCheckout={() => encounterPrimaryAction(item)}
                        submitMode={submitMode}
                        onTypeChanged={handleTypeChanged}
                        isCheckoutLoading={loading}
                      />
                    ))}
                  </div>
                )}

                {pagination.totalCount > 0 && (
                  <div className="encounters-pagination">
                    <Pagination
                      current={pagination.currentPage}
                      total={pagination.totalCount}
                      pageSize={pagination.pageSize}
                      simple={isCompactView}
                      showSizeChanger={!isMobileView}
                      showTotal={isMobileView ? undefined : (total, range) => `${range[0]}-${range[1]} of ${total} encounters`}
                      onChange={handlePageChange}
                      onShowSizeChange={handlePageChange}
                      pageSizeOptions={['10', '20', '50', '100']}
                    />
                  </div>
                )}
              </DashboardSection>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export const CheckinListAnalytics = ({ filters = {}, packages = [], type = null }) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  const [appliedFilters, setAppliedFilters] = useState({ ...filters });

  const pickerRef = useRef();

  const loadOrgs = async (search) => {
    return myRequestObj('organizations')
      .post({ mode: 'mini', search })
      .then(data => data?.data)
      .then(data => {
        return data
          ? data?.data?.map(org => ({
            label: org.name,
            value: org.social_no,
            key: org.id,
          }))
          : [];
      });
  };

  const fetchAnalytics = (payload = {}, d_type = type) => {
    setLoading(true);
    return myRequestObj(d_type === 1 ? 'encounterDashAlt' : 'encounterDash')
      .post({ ...payload, ...(d_type !== null ? { type: d_type } : {}) })
      .then(res => res?.data)
      .then(data => {
        if (Number(data?.error) !== 0) {
          notification.error({ message: 'Error Fetching Data' });
          return;
        }
        setData(data?.data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAnalytics({ type, ...appliedFilters });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const relaod = () => {
    setAppliedFilters({});
    pickerRef.current?.clearDates?.();
    fetchAnalytics({ type });
  };

  const loadData = () => {
    fetchAnalytics({ type, ...appliedFilters });
  };

  const columns = [
    {
      title: 'Diagnosis',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Checkin Type',
      dataIndex: 'Checkin Name',
      key: 'Checkin Name',
    },
    {
      title: 'No. of Occurence',
      dataIndex: 'count',
      key: 'count',
      render: (text) => (
        <Statistic
          value={text}
          formatter={formatter}
        />
      ),
    },
  ];

  return (
    <>
      <Row gutter={[12, 12]} justify={'center'} align={'middle'}>
        <Col>
          <DebounceSelect
            value={appliedFilters?.social}
            fetchOptions={loadOrgs}
            onChange={(value) => setAppliedFilters(d => ({ ...d, social: value }))}
            placeholder="Org name or code"
            style={{ width: '100%' }}
          />
        </Col>
        <Col>
          <ConfigurableRangePicker
            ref={pickerRef}
            minYear='2025'
            onChange={value => setAppliedFilters(d => ({ ...d, ...value }))}
            pickerType="day"
          />
        </Col>
        <Col>
          <Row gutter={8}>
            <Col>
              <Button
                style={{ width: '100%', placeContent: 'center' }}
                onClick={loadData}
              >
                Reload
              </Button>
            </Col>
            <Col>
              <Button
                style={{ width: '100%' }}
                onClick={relaod}
              >
                Reset
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
      <Divider orientation='left' orientationMargin={0}><strong>What's happening today?</strong></Divider>
      <Row>
        {loading ? (
          <Loader size={50} />
        ) : (
          <>
            <Divider orientationMargin={0} dashed orientation="left">Encounters</Divider>
            {data?.encounters ? (
              <Row justify={'start'} align={'middle'}>
                {Object.keys(data.encounters)?.map(d => (
                  <Col key={d}>
                    <Card bordered={false}>
                      <Statistic
                        title={<strong>{d}</strong>}
                        value={data?.encounters[d]}
                        formatter={formatter}
                      />
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <Empty description={<strong>No Data</strong>} />
            )}

            <Divider orientationMargin={0} dashed orientation="left">Pre-authorization Requests</Divider>
            {data?.pa ? (
              <Row justify={'start'} align={'middle'}>
                {Object.keys(data.pa)?.map(d => (
                  <Col key={d}>
                    <Card bordered={false}>
                      <Statistic
                        title={<strong>{d}</strong>}
                        value={data?.pa[d]}
                        formatter={formatter}
                      />
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <Empty description={<strong>No Data</strong>} />
            )}

            <Divider orientationMargin={0} dashed orientation="left">Benefit items (from item mapping)</Divider>
            {Array.isArray(data?.mapped) && data.mapped.length ? (
              <Row justify={'start'} align={'middle'}>
                {data.mapped.map((m) => (
                  <Col key={m.benefit_item}>
                    <Card bordered={false}>
                      <Statistic title={<strong>{m.benefit_item}</strong>} value={m.encounters} formatter={formatter} />
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <Empty description={<span>No mapped items in this period — surgery and consultation counts come only from item mapping now.</span>} />
            )}

            <Divider orientationMargin={0} dashed orientation="left">
              Encounter Diagnoses - ({data?.diagnosis?.length || 0})
            </Divider>
            {data?.diagnosis ? (
              <Row justify={'start'} align={'middle'}>
                <Table columns={columns} dataSource={data?.diagnosis} pagination={false} />
              </Row>
            ) : (
              <Empty description={<strong>No Data</strong>} />
            )}
          </>
        )}
      </Row>
    </>
  );
};

export const CheckinListAnalyticsInPatioent = (filters = {}, packages = []) => (
  <CheckinListAnalytics type={1} filters={filters} packages={packages} />
);

export default CheckinListHook;

const EncounterCard = ({ item, userRoleId, onViewPA, onCheckout, onTypeChanged, isCheckoutLoading, submitMode = false }) => {
  const detailsRef = useRef(null);

  const canViewPA = item?.code && [1, 2, 3].includes(userRoleId);
  const canCheckout = submitMode
    ? canSubmitClaimFor(item, userRoleId)
    : canCheckoutFor(item, userRoleId);

  return (
    <>
      <EncounterListingCard
        record={item}
        onClick={() => detailsRef.current?.open()}
        actions={
          <>
            {canViewPA ? (
              <Button
                type="link"
                size="small"
                className="listing-card__action"
                onClick={(event) => {
                  event.stopPropagation();
                  onViewPA?.(item);
                }}
              >
                Pre-auth
              </Button>
            ) : null}
            {canCheckout ? (
              <Button
                type="link"
                size="small"
                className="listing-card__action"
                loading={isCheckoutLoading}
                onClick={(event) => {
                  event.stopPropagation();
                  onCheckout?.(item);
                }}
              >
                {submitMode ? SUBMIT_CLAIM_LABEL : 'Checkout'}
              </Button>
            ) : null}
          </>
        }
      />
      <EncounterDetailsModal
        ref={detailsRef}
        data={item}
        userRoleId={userRoleId}
        onViewPA={onViewPA}
        onCheckout={onCheckout}
        submitMode={submitMode}
        onTypeChanged={onTypeChanged}
        isCheckoutLoading={isCheckoutLoading}
      />
    </>
  );
};